// src/App.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useI18n } from './i18n/i18n'; // ✅ Додано імпорт useI18n
import { NotificationProvider } from './context/NotificationContext';

import initializeUsers from './utils/initUsers';
import { getSessionItem, removeSessionItem, getData } from './services/storageService';

// Імпорт сторінок
import DashboardRouter from './pages/DashboardRouter';
import DashboardOperator from './pages/DashboardOperator';
import DashboardSupervisor from './pages/DashboardSupervisor';
import DashboardTechnician from './pages/DashboardTechnician';

import MachineStatusPage from './pages/MachineStatusPage';
import MachineDetailsPage from './pages/MachineDetailsPage';

import FormNew from './pages/FormNew';
import Archive from './pages/Archive';
import Reports from './pages/Reports';
import ViewRecord from './pages/ViewRecord';
import OpenShift from './pages/OpenShift';

import Login from './pages/Login';
import Register from './pages/Register';
import Settings from './pages/Settings';
import LicensePage from './pages/LicensePage';

import AnalyticsPage from './pages/AnalyticsPage';
import UsersAdmin from './pages/UsersAdmin';

// Сторінки персоналу
import StaffDirectory from './pages/StaffDirectory';
import StaffRosterPage from './pages/StaffRosterPage';
import StaffRoster from './pages/StaffRoster';
import IroningAssignments from './pages/IroningAssignments';
import About from './pages/About';
import EquipmentAdminPage from './pages/EquipmentAdminPage';
import MaintenanceLogPage from './pages/MaintenanceLogPage';
import MaintenanceCalendarPage from './pages/MaintenanceCalendarPage';
import TechnicianReportPage from './pages/TechnicianReportPage';
import TechnicianFormPage from './pages/TechnicianFormPage';
import EmployeeProfile from './pages/EmployeeProfile';
import AttendanceSystem from './pages/AttendanceSystem';
import StaffAnalytics from './pages/StaffAnalytics';
import TimeOffManagement from './pages/TimeOffManagement';
import AdvancedReportsPage from './pages/AdvancedReportsPage';

// Інциденти та архів
import IncidentsCreatePage from './features/incidents/IncidentsCreatePage';
import IncidentsListPage from './features/incidents/IncidentsListPage';
import ArchiveIncidentsPage from './features/archive/ArchiveIncidentsPage';
import OperatorReports from './pages/Archive/OperatorReports';
import TechnicianReports from './pages/Archive/TechnicianReports';
import Misc from './pages/Archive/Misc';
import IncidentViewPage from './features/incidents/IncidentViewPage';
import ArchiveDashboard from './pages/Archive/ArchiveDashboard';
import IncidentsAnalyticsDashboard from './features/incidents/IncidentsAnalyticsDashboard';
import IncidentsPatternsAnalysis from './features/incidents/IncidentsPatternsAnalysis';
import IncidentsAIDashboard from './features/incidents/IncidentsAIDashboard';
import IncidentsIoTDashboard from './features/incidents/IncidentsIoTDashboard';
import ImageAnalysisMainPage from './features/incidents/ImageAnalysisMainPage';
import ImageAnalysisPage from './features/incidents/ImageAnalysisPage';

// Нові сторінки для аналізу лічильників
import MeterAnalysisPage from './features/MeterAnalysis/MeterAnalysisPage';
import MeterDetailPage from './features/MeterAnalysis/MeterDetailPage';

// Іконки прапорів
import 'flag-icons/css/flag-icons.min.css';

// Аналітика тунелю
import TunnelAnalysisPage from './features/TunnelAnalysis/TunnelAnalysisPage';
import TunnelDetailPage from './features/TunnelAnalysis/TunnelDetailPage';

// Аналітика пральних машин
import WasherAnalysisPage from './features/WasherAnalysis/WasherAnalysisPage';
import WasherDetailPage from './features/WasherAnalysis/WasherDetailPage';

// Аналітика ваги по клієнтам:
import ClientAnalysisPage from './features/ClientAnalysis/ClientAnalysisPage';
import ClientDetailPage from './features/ClientAnalysis/ClientDetailPage';
import TrendReportPage from './features/Reports/TrendReportPage';

// Прогноз АІ - ДОДАЄМО ІМПОРТ
import ForecastPage from './features/Forecast/ForecastPage'; // ✅ ДОДАНО

// Компоненти архіву - ДОДАЄМО ІМПОРТИ
import MeterReports from './pages/Archive/MeterReports'; // ✅ ДОДАНО

// *** ДОДАЄМО AI-РЕКОМЕНДАЦІЇ ***
import useAIRecommendations from './features/AIRecommendations/hooks/useAIRecommendations';
import AIDashboard from './features/AIDashboard/AIDashboard';
import WhatIfAnalysisPage from './features/Analytics/WhatIfAnalysisPage';

import TechnicianStatsPage from './features/TechnicianAnalysis/TechnicianStatsPage';
import RecurringIssuesPage from './features/TechnicianAnalysis/RecurringIssuesPage';
import TechnicianReportViewPage from './features/TechnicianAnalysis/TechnicianReportViewPage';
import IssueGallery from './features/TechnicianAnalysis/IssueGallery';
import TechnicianMLAnalysis from './features/TechnicianAnalysis/TechnicianMLAnalysis';
import PartsInventory from './features/TechnicianAnalysis/PartsInventory';
import PartsAnalytics from './features/TechnicianAnalysis/PartsAnalytics';
import PartsManager from './features/TechnicianAnalysis/PartsManager';
import MobileWorkOrders from './features/FieldService/MobileWorkOrders';
import WorkOrderAnalytics from './features/FieldService/WorkOrderAnalytics';
import FutureIntegrations from './components/FutureIntegrations';
import TrainingManagement from './pages/TrainingManagement';
import KPIManagement from './pages/KPIManagement';
import ManualsPage from './pages/ManualsPage';
import ProductionPlanning from './pages/ProductionPlanning';
import AnalyticsDashboard from './pages/AnalyticsDashboard';

import ServerAdmin from './pages/ServerAdmin';
import DeviceRegistration from './pages/DeviceRegistration';
import SyncStatus from './pages/SyncStatus';

import Layout from './components/Layout';
import { Box, Typography } from '@mui/material';

function PrivateRoute({ children, user, roles = [] }) {
  if (!user) return <Navigate to="/" replace />;
  if (roles.length > 0 && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Гейт для Settings: admin або технік з prefs.techCanOpenSettings === true
function SettingsRoute({ user }) {
  const [ready, setReady] = useState(false);
  const [allow, setAllow] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const checkAccess = async () => {
      if (!user) {
        if (isMounted) {
          setReady(true);
          setAllow(false);
        }
        return;
      }
      
      if (user.role === 'admin') {
        if (isMounted) {
          setReady(true);
          setAllow(true);
        }
        return;
      }
      
      if (user.role === 'technician') {
        try {
          const prefs = await getData('app_prefs');
          const ok = !!prefs?.techCanOpenSettings;
          if (isMounted) {
            setAllow(ok);
            setReady(true);
          }
        } catch (error) {
          console.error('Помилка при отриманні налаштувань:', error);
          if (isMounted) {
            setAllow(false);
            setReady(true);
          }
        }
        return;
      }
      
      if (isMounted) {
        setAllow(false);
        setReady(true);
      }
    };

    checkAccess();
    
    return () => {
      isMounted = false;
    };
  }, [user]);

  if (!user) return <Navigate to="/" replace />;
  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography variant="h6">Завантаження...</Typography>
      </Box>
    );
  }
  if (!allow) return <Navigate to="/dashboard" replace />;
  
  return <Settings />;
}

// 🔥 ВИПРАВЛЕННЯ: Виносимо головний компонент додатка
function AppContent({ user, onLogout, onLogin, lang }) { // ✅ ДОДАНО onLogin
  // 🔥 ВИПРАВЛЕННЯ: AI рекомендації тепер тут, після NotificationProvider
  const { analyzeData } = useAIRecommendations();

  useEffect(() => {
    // 🔥 ВИПРАВЛЕННЯ: AI аналіз тепер тут, де NotificationProvider вже доступний
    if (user) {
      setTimeout(() => {
        analyzeData();
      }, 3000);
    }
  }, [user, analyzeData]);

  return (
    <Layout user={user} onLogout={onLogout}>
      <Routes>
        {/* Публічні маршрути */}
        <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <Login onLogin={onLogin} />} />
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login onLogin={onLogin} />} />
        <Route path="/register" element={<Register />} />
        <Route path="/license" element={<LicensePage />} />
        <Route path="/" element={<Navigate to={user ? "/dashboard" : "/login"} replace />} />
        <Route path="/register" element={<Register />} />

        {/* Settings через гейт */}
        <Route path="/settings" element={<SettingsRoute user={user} />} />

        {/* Дашборди */}
        <Route path="/dashboard" element={<PrivateRoute user={user}><DashboardRouter user={user} /></PrivateRoute>} />
        <Route path="/dashboard/operator" element={<PrivateRoute user={user}><DashboardOperator user={user} /></PrivateRoute>} />
        <Route path="/dashboard/supervisor" element={<PrivateRoute user={user}><DashboardSupervisor user={user} /></PrivateRoute>} />
        <Route path="/dashboard/technician" element={<PrivateRoute user={user}><DashboardTechnician user={user} /></PrivateRoute>} />
        
        {/* НОВІ МАРШРУТИ АРХІВУ */}
        <Route path="/archive" element={<PrivateRoute user={user}><ArchiveDashboard /></PrivateRoute>} />
        <Route path="/archive/operator" element={<PrivateRoute user={user}><OperatorReports /></PrivateRoute>} />
        <Route path="/archive/technician" element={<PrivateRoute user={user}><TechnicianReports /></PrivateRoute>} />
        <Route path="/archive/meters" element={<PrivateRoute user={user}><MeterReports /></PrivateRoute>} />
        <Route path="/archive/misc" element={<PrivateRoute user={user}><Misc /></PrivateRoute>} />

        {/* Основні сторінки */}
        <Route path="/machines" element={<PrivateRoute user={user}><MachineStatusPage /></PrivateRoute>} />
        <Route path="/machine-details/:machineId" element={<PrivateRoute user={user}><MachineDetailsPage /></PrivateRoute>} />
        <Route path="/form" element={<PrivateRoute user={user}><FormNew /></PrivateRoute>} />
        <Route path="/archive-old" element={<PrivateRoute user={user}><Archive /></PrivateRoute>} />
        <Route path="/reports" element={<PrivateRoute user={user}><Reports /></PrivateRoute>} />
        <Route path="/view/:id" element={<PrivateRoute user={user}><ViewRecord /></PrivateRoute>} />
        <Route path="/open-shift" element={<PrivateRoute user={user}><OpenShift /></PrivateRoute>} />
        <Route path="/about" element={<About />} />

        {/* Технічні сторінки */}
        <Route path="/equipment" element={<PrivateRoute user={user} roles={['admin','technician']}><EquipmentAdminPage /></PrivateRoute>} />
        <Route path="/maintenance" element={<PrivateRoute user={user} roles={['admin','technician']}><MaintenanceLogPage user={user} /></PrivateRoute>} />
        <Route path="/maintenance-calendar" element={<PrivateRoute user={user} roles={['admin','technician','supervisor']}><MaintenanceCalendarPage /></PrivateRoute> } />
        <Route path="/technician-report" element={<PrivateRoute user={user} roles={['admin','technician','supervisor']}><TechnicianReportPage user={user} /></PrivateRoute>} />
        <Route path="/technician-form" element={<PrivateRoute user={user} roles={['admin','technician','supervisor']}><TechnicianFormPage user={user} /></PrivateRoute> } />
        <Route path="/production-planning" element={<PrivateRoute user={user} roles={['admin', 'supervisor', 'operator', 'technician']}><ProductionPlanning user={user} /></PrivateRoute> } />

        {/* Аналітика та адміністрування */}
        <Route path="/analytics" element={<PrivateRoute user={user}><AnalyticsPage /></PrivateRoute>} />
        <Route path="/users-admin" element={<PrivateRoute user={user} roles={['admin']}><UsersAdmin /></PrivateRoute> } />
        <Route path="/analytics-dashboard" element={<PrivateRoute user={user} roles={['admin', 'supervisor', 'technician']}><AnalyticsDashboard user={user} /></PrivateRoute>} />

        {/* Сторінки персоналу */}
        <Route path="/staff" element={<PrivateRoute user={user} roles={['admin','supervisor']}><StaffDirectory /></PrivateRoute> } />
        <Route path="/staff-roster" element={<PrivateRoute user={user} roles={['admin','supervisor']}><StaffRoster /></PrivateRoute> } />
        <Route path="/iron-assignments" element={<PrivateRoute user={user} roles={['admin','technician','supervisor']}><IroningAssignments /></PrivateRoute> } />
        {/* Система управління персоналом */}
        <Route path="/attendance" element={<PrivateRoute user={user} roles={['admin','supervisor']}><AttendanceSystem /></PrivateRoute> } />
        <Route path="/employee/:id" element={<PrivateRoute user={user} roles={['admin','supervisor']}><EmployeeProfile /> </PrivateRoute> } />
        <Route path="/staff-analytics" element={<PrivateRoute user={user} roles={['admin','supervisor']}><StaffAnalytics /></PrivateRoute> } />
        <Route path="/timeoff" element={<TimeOffManagement />} />
        <Route path="/reports" element={<AdvancedReportsPage />} />

        {/* Нові сторінки для аналізу лічильників */}
        <Route path="/meter-analysis" element={<PrivateRoute user={user}><MeterAnalysisPage user={user} /></PrivateRoute> } />
        <Route path="/meter-analysis/:meterKey" element={<PrivateRoute user={user}><MeterDetailPage user={user} /></PrivateRoute> } />

        {/* Архів */}
        <Route path="/archive/operator-old" element={<PrivateRoute user={user}><OperatorReports /></PrivateRoute>} />
        <Route path="/archive/technician-old" element={<PrivateRoute user={user}><TechnicianReports /></PrivateRoute>} />
        <Route path="/archive/misc-old" element={<PrivateRoute user={user}><Misc /></PrivateRoute>} />

        {/* Інциденти */}
        <Route path="/archive/incidents" element={<PrivateRoute user={user}><ArchiveIncidentsPage /></PrivateRoute>} />
        <Route path="/incidents/create" element={<PrivateRoute user={user}><IncidentsCreatePage user={user} /></PrivateRoute>} />
        <Route path="/incidents/list" element={<PrivateRoute user={user}><IncidentsListPage user={user} /></PrivateRoute>} />
        <Route path="/incidents/:id" element={<PrivateRoute user={user}><IncidentViewPage user={user} /></PrivateRoute>} />
        <Route path="/incidents/analytics" element={<PrivateRoute user={user}><IncidentsAnalyticsDashboard /></PrivateRoute>} />
        <Route path="/incidents/patterns" element={<PrivateRoute user={user}><IncidentsPatternsAnalysis /></PrivateRoute>} />
        <Route path="/incidents/ai-dashboard" element={<PrivateRoute user={user}><IncidentsAIDashboard /></PrivateRoute>} />
        <Route path="/incidents/iot-dashboard" element={<PrivateRoute user={user}><IncidentsIoTDashboard /></PrivateRoute>} />
        <Route path="/incidents/:id/image-analysis" element={<PrivateRoute user={user}><ImageAnalysisPage /></PrivateRoute>} />
        <Route path="/incidents/image-analysis" element={<PrivateRoute user={user}><ImageAnalysisMainPage /></PrivateRoute>} />
        <Route path="/incidents/:id/image-analysis" element={<PrivateRoute user={user}><ImageAnalysisPage /></PrivateRoute>} />

        {/* Аналітика тунелю */}
        <Route path="/tunnel-analysis" element={<PrivateRoute user={user}><TunnelAnalysisPage user={user} /></PrivateRoute>} />
        <Route path="/tunnel-analysis/:programKey" element={<PrivateRoute user={user}><TunnelDetailPage user={user} /></PrivateRoute>} />

        {/* Аналітика пральних машин */}
        <Route path="/washer-analysis" element={<PrivateRoute user={user}><WasherAnalysisPage user={user} /></PrivateRoute>} />
        <Route path="/washer-analysis/:programKey" element={<PrivateRoute user={user}><WasherDetailPage user={user} /></PrivateRoute>}/>

        {/* Аналітика ваги по клієнтам */}
        <Route path="/client-analysis" element={<PrivateRoute user={user}><ClientAnalysisPage user={user} /></PrivateRoute>} />
        <Route path="/client-analysis/:clientKey" element={<PrivateRoute user={user}><ClientDetailPage user={user} /></PrivateRoute>} />

        {/* Прогнози */}
        <Route path="/forecast" element={<PrivateRoute user={user}><ForecastPage /></PrivateRoute>} />
        <Route path="/trend-reports" element={<PrivateRoute user={user}><TrendReportPage /></PrivateRoute>} />

        <Route path="/ai-dashboard" element={<PrivateRoute user={user}><AIDashboard /></PrivateRoute>} />
        <Route path="/what-if-analysis" element={<PrivateRoute user={user}><WhatIfAnalysisPage /></PrivateRoute>} />

        {/* Технік */}
        <Route path="/technician-stats" element={<PrivateRoute user={user} roles={['admin','technician','supervisor']}><TechnicianStatsPage user={user} /></PrivateRoute>} />
        <Route path="/recurring-issues" element={<PrivateRoute user={user} roles={['admin','technician','supervisor']}><RecurringIssuesPage user={user} /></PrivateRoute>} />
        <Route path="/technician-report/:reportId" element={<PrivateRoute user={user} roles={['admin','technician','supervisor']}><TechnicianReportViewPage /></PrivateRoute>} />
        <Route path="/issue-gallery" element={<PrivateRoute user={user} roles={['admin','technician','supervisor']}><IssueGallery user={user} /></PrivateRoute>} />
        <Route path="/technician-ml-analysis" element={<PrivateRoute user={user} roles={['admin','technician','supervisor']}><TechnicianMLAnalysis user={user} /></PrivateRoute>} />
        <Route path="/parts-inventory" element={<PrivateRoute user={user} roles={['admin','technician','supervisor']}><PartsInventory user={user} /></PrivateRoute>} />
        <Route path="/parts-analytics" element={<PrivateRoute user={user} roles={['admin','technician','supervisor']}><PartsAnalytics user={user} /></PrivateRoute>} />
        <Route path="/parts-manager" element={<PrivateRoute user={user} roles={['admin','technician','supervisor']}><PartsManager user={user} /></PrivateRoute>} />
        <Route path="/mobile-work-orders" element={<PrivateRoute user={user} roles={['admin','technician','supervisor']}><MobileWorkOrders user={user} /></PrivateRoute>} />
        <Route path="/work-order-analytics" element={<PrivateRoute user={user} roles={['admin','supervisor']}><WorkOrderAnalytics user={user} /></PrivateRoute>} />

        {/* Майбутнє */}
        <Route path="/future-integrations" element={<PrivateRoute user={user}><FutureIntegrations /></PrivateRoute>} />
        <Route path="/training-management" element={<PrivateRoute user={user}><TrainingManagement /></PrivateRoute>} />
        <Route path="/kpi-management" element={<PrivateRoute user={user}><KPIManagement /></PrivateRoute>} />
        <Route path="/manuals-guides" element={<PrivateRoute user={user}><ManualsPage /></PrivateRoute>} />

        <Route path="/server-admin" element={<PrivateRoute user={user} roles={['admin']}><ServerAdmin /></PrivateRoute>} />
        <Route path="/device-registration" element={<PrivateRoute user={user}><DeviceRegistration /></PrivateRoute>} />
        <Route path="/sync-status" element={<PrivateRoute user={user}><SyncStatus /></PrivateRoute>} />

        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loadingApp, setLoadingApp] = useState(true);
  const { lang } = useI18n(); // ✅ Додано підписку на зміни мови

  useEffect(() => {
    initializeUsers().catch(error => {
      console.error('Помилка ініціалізації користувачів:', error);
    });
  }, []);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        const sessionUser = await getSessionItem('currentUser');
        if (sessionUser) {
          setUser(sessionUser);
        }
      } catch (error) {
        console.error('Помилка при ініціалізації додатка:', error);
      } finally {
        setLoadingApp(false);
      }
    };

    initializeApp();
  }, []);

  // ✅ ВИПРАВЛЕННЯ: Додаємо handleLogin
  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await removeSessionItem('currentUser');
      setUser(null);
    } catch (error) {
      console.error('Помилка при виході:', error);
    }
  };

  if (loadingApp) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh', 
        backgroundColor: '#f0f2f5' 
      }}>
        <Typography variant="h5">Cargando aplicación...</Typography>
      </Box>
    );
  }

  return (
    <div key={lang}> {/* ✅ key примусово перерендерить все при зміні мови */}
      {/* ✅ ПРАВИЛЬНИЙ ПОРЯДОК: NotificationProvider обгортає весь додаток */}
      <NotificationProvider>
        <AppContent 
          user={user} 
          onLogout={handleLogout} 
          onLogin={handleLogin} // ✅ ДОДАНО onLogin
          lang={lang}
        />
      </NotificationProvider>
    </div>
  );
}